function readFile(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

const S = new Uint8Array([
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 4, 11, 16,
  23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
])

const K = new Uint32Array(64)
const K_values = new Float64Array(64)
for (let i = 0; i < 64; i++) {
  K_values[i] = Math.abs(Math.sin(i + 1))
  K[i] = (K_values[i] * 0x100000000) >>> 0
}

function leftRotate(x: number, n: number): number {
  return ((x << n) | (x >>> (32 - n))) >>> 0
}

const CHUNK_SIZE = 16384 // 16KB chunks for large files

function md5Core(message: Uint8Array): Uint8Array {
  let a0 = 0x67452301
  let b0 = 0xefcdab89
  let c0 = 0x98badcfe
  let d0 = 0x10325476

  const paddedLength = ((((message.length + 8) >>> 6) + 1) << 6) >>> 0
  const paddedMsg = new Uint8Array(paddedLength)
  paddedMsg.set(message)
  paddedMsg[message.length] = 0x80

  const bitLength = message.length * 8
  const dv = new DataView(paddedMsg.buffer)
  dv.setUint32(paddedLength - 8, bitLength >>> 0, true)
  dv.setUint32(paddedLength - 4, (bitLength / 0x100000000) >>> 0, true)

  for (let i = 0; i < paddedLength; i += 64) {
    const chunk = new Uint32Array(16)
    for (let j = 0; j < 16; j++) {
      chunk[j] = dv.getUint32(i + j * 4, true)
    }

    let A = a0
    let B = b0
    let C = c0
    let D = d0

    for (let j = 0; j < 64; j++) {
      let F: number, g: number

      if (j < 16) {
        F = (B & C) | (~B & D)
        g = j
      } else if (j < 32) {
        F = (D & B) | (~D & C)
        g = (5 * j + 1) % 16
      } else if (j < 48) {
        F = B ^ C ^ D
        g = (3 * j + 5) % 16
      } else {
        F = C ^ (B | ~D)
        g = (7 * j) % 16
      }

      F = (F + A + K[j] + chunk[g]) >>> 0
      A = D
      D = C
      C = B
      B = (B + leftRotate(F, S[j])) >>> 0
    }

    a0 = (a0 + A) >>> 0
    b0 = (b0 + B) >>> 0
    c0 = (c0 + C) >>> 0
    d0 = (d0 + D) >>> 0
  }

  const result = new Uint8Array(16)
  new DataView(result.buffer).setUint32(0, a0, true)
  new DataView(result.buffer).setUint32(4, b0, true)
  new DataView(result.buffer).setUint32(8, c0, true)
  new DataView(result.buffer).setUint32(12, d0, true)
  return result
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export async function md5(str: string): Promise<string>
export async function md5(file: File): Promise<string>
export async function md5(input: File | string): Promise<string> {
  if (typeof input === 'string') {
    const encoder = new TextEncoder()
    const data = encoder.encode(input)
    return bytesToHex(md5Core(data))
  }

  const file = input as File
  if (file.size <= CHUNK_SIZE) {
    const arrayBuffer = (await readFile(file)) as ArrayBuffer
    const data = new Uint8Array(arrayBuffer)
    return bytesToHex(md5Core(data))
  }

  // Process large files in chunks
  let a0 = 0x67452301
  let b0 = 0xefcdab89
  let c0 = 0x98badcfe
  let d0 = 0x10325476
  let processedBytes = 0

  const reader = file.stream().getReader()

  while (processedBytes < file.size) {
    const { value, done } = await reader.read()
    if (done) break

    const result = md5Core(value)
    const [a, b, c, d] = new Uint32Array(result.buffer)

    a0 = (a0 + a) >>> 0
    b0 = (b0 + b) >>> 0
    c0 = (c0 + c) >>> 0
    d0 = (d0 + d) >>> 0

    processedBytes += value.length
  }

  const result = new Uint8Array(16)
  new DataView(result.buffer).setUint32(0, a0, true)
  new DataView(result.buffer).setUint32(4, b0, true)
  new DataView(result.buffer).setUint32(8, c0, true)
  new DataView(result.buffer).setUint32(12, d0, true)

  return bytesToHex(result)
}
